import { loadTemplate, templateStore } from '../template/index.js';
import { parseToDateString, getStartDateOfMonth } from './utilities.js';

export class DatePicker {

	constructor(options) {
		this.container = options?.container || document.getElementById('wpcore-date-picker');
		this.selectedDate = new Date();
		this.selectedDate.setHours(0, 0, 0, 0);
		this.currentMonth = new Date().getMonth();
		this.currentYear = new Date().getFullYear();
		this.options = options;
		this.init();
	}

	async loadTemplate() {
		return Promise.all([
			loadTemplate('date-picker/grid-days.ejs'),
			loadTemplate('date-picker/cell-day.ejs'),
			loadTemplate('date-picker/controls.ejs')
		]);
	}

	async init() {
		if (!this.container) {
			return;
		}

		await this.loadTemplate();

		// eslint-disable-next-line no-undef
		const controls = ejs.render(templateStore.get('date-picker/controls.ejs'));
		this.container.insertAdjacentHTML('beforeend', controls);
		// eslint-disable-next-line no-undef
		const gridDays = ejs.render(templateStore.get('date-picker/grid-days.ejs'));
		this.container.insertAdjacentHTML('beforeend', gridDays);

		this.monthYearEl = this.container.querySelector('#month-year');
		this.prevButton = this.container.querySelector('#prev-month');
		this.nextButton = this.container.querySelector('#next-month');
		this.gridDays = this.container.querySelector('#grid-days');
		this.statusOfDates = this.options?.renderStatusOfDates?.(this.currentMonth, this.currentYear) || {};
		this.render();
		this.renderHandler();
		this.options?.afterInit && this.options.afterInit(this.selectedDate);
	}

	render() {
		this.renderControls();
		this.renderDayInMonth();
	}

	renderDayInMonth() {

		if (!this.gridDays) {
			return;
		}
		const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
		const firstDay = getStartDateOfMonth(this.currentMonth, this.currentYear);
		const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const minDate = this.options?.minDate || today;

		for (let i = 0; i < startOffset; i++) {
			const empty = document.createElement('div');
			this.gridDays.appendChild(empty);
		}

		// Find first available date if selected date is not available
		const selectedDateStr = parseToDateString(this.selectedDate);
		const selectedStatus = this.statusOfDates[selectedDateStr] || 'disabled';
		const isSelectedInCurrentMonth = this.selectedDate.getMonth() === this.currentMonth && this.selectedDate.getFullYear() === this.currentYear;

		if (isSelectedInCurrentMonth && selectedStatus !== 'default') {
			// Find the first available date in this month
			let found = false;
			for (let d = 1; d <= daysInMonth; d++) {
				const candidate = new Date(this.currentYear, this.currentMonth, d);
				if (candidate >= minDate) {
					const candidateStr = parseToDateString(candidate);
					const candidateStatus = this.statusOfDates[candidateStr] || 'disabled';
					if (candidateStatus === 'default') {
						this.selectedDate = candidate;
						this.selectedDate.setHours(0, 0, 0, 0);
						found = true;
						break;
					}
				}
			}
		}

		for (let day = 1; day <= daysInMonth; day++) {
			const dateObj = new Date(this.currentYear, this.currentMonth, day);
			const dateStr = parseToDateString(dateObj);
			const isSelected = parseToDateString(this.selectedDate) === dateStr;
			const isPast = dateObj < minDate;
			const dateStatus = this.statusOfDates[dateStr] || 'disabled';

			let status;
			if (isPast) {
				status = 'past';
			} else if (isSelected && dateStatus === 'default') {
				status = 'selected';
			} else {
				status = dateStatus;
			}

			// eslint-disable-next-line no-undef
			const cellDay = ejs.render(templateStore.get('date-picker/cell-day.ejs'), {
				day,
				status
			});

			this.gridDays.insertAdjacentHTML('beforeend', cellDay);
		}

	}

	renderControls() {
		const fullMonths = [
			wpcore.direct_t({ en: 'January', fr: 'Janvier' }),
			wpcore.direct_t({ en: 'February', fr: 'Février' }),
			wpcore.direct_t({ en: 'March', fr: 'Mars' }),
			wpcore.direct_t({ en: 'April', fr: 'Avril' }),
			wpcore.direct_t({ en: 'May', fr: 'Mai' }),
			wpcore.direct_t({ en: 'June', fr: 'Juin' }),
			wpcore.direct_t({ en: 'July', fr: 'Juillet' }),
			wpcore.direct_t({ en: 'August', fr: 'Août' }),
			wpcore.direct_t({ en: 'September', fr: 'Septembre' }),
			wpcore.direct_t({ en: 'October', fr: 'Octobre' }),
			wpcore.direct_t({ en: 'November', fr: 'Novembre' }),
			wpcore.direct_t({ en: 'December', fr: 'Décembre' }),
		];
		const monthYear = `${fullMonths[this.currentMonth]} ${this.currentYear}`;
		this.monthYearEl && (this.monthYearEl.textContent = monthYear);
	}

	renderHandler() {
		this.prevButton?.addEventListener('click', () => this.handleChangeMonth('prev'));
		this.nextButton?.addEventListener('click', () => this.handleChangeMonth('next'));

		this.gridDays?.addEventListener('click', (e) => this.handleChangeDate(e));
	}

	handleChangeMonth(type) {
		this.gridDays.innerHTML = '';
		if (type === 'prev') {
			this.currentMonth--;
			if (this.currentMonth < 0) {
				this.currentMonth = 11;
				this.currentYear--;
			}
		} else {
			this.currentMonth++;
			if (this.currentMonth > 11) {
				this.currentMonth = 0;
				this.currentYear++;
			}
		}
		this.statusOfDates = this.options?.renderStatusOfDates?.(this.currentMonth, this.currentYear) || {};
		this.render();
	}

	handleChangeDate(e) {
		const dayWrapper = e.target.closest('[data-day]');
		if (!dayWrapper) return;

		const day = dayWrapper.dataset.day;
		const selectedDate = new Date(this.currentYear, this.currentMonth, day);
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (this.statusOfDates[parseToDateString(selectedDate)] !== 'default') {
			return;
		}
		if (selectedDate < today) {
			return;
		}
		this.selectedDate = selectedDate;
		this.selectedDate.setHours(0, 0, 0, 0);
		this.gridDays.innerHTML = '';
		this.renderDayInMonth();

		this.options?.onDateChange && this.options.onDateChange(this.selectedDate);
	}
}
